/* assets/lauten-tag-ajax.js
 * AJAX tag filtering for Lauten subfilter (keeps sidebar intact)
 * - Replaces: #ProductGridContainer
 * - ALSO replaces: .laut-subfacet (so tags regenerate when collection changes)
 * - Syncs checkbox UI state from URL after each load
 * - Supports single or multi mode via: data-laut-mode="single|multi"
 *
 * Clear button behavior (UPDATED):
 * - Clear button shows ONLY when at least one tag is active
 * - Clear button clears ONLY tags (keeps current main collection)
 */

(function () {
  const LABEL_SEL = ".laut-subfacet__label[data-laut-tag-handle]";
  const GRID_SEL = "#ProductGridContainer";
  const SUBFACET_SEL = ".laut-subfacet";
  const SECTION_ID = "main-collection-product-grid";

  const setLoading = (isLoading) => {
    const grid = document.querySelector(GRID_SEL);
    if (!grid) return;
    grid.classList.toggle("is-loading", !!isLoading);
    const overlay = grid.querySelector(".loading-overlay");
    if (overlay) overlay.style.display = isLoading ? "block" : "";
  };

  const fetchSectionHTML = async (url) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("section_id", SECTION_ID);

    const res = await fetch(u.toString(), {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });

    if (!res.ok) throw new Error("Fetch failed");
    return await res.text();
  };

  const parseFragment = (html) => {
    const wrap = document.createElement("div");
    wrap.innerHTML = html;
    return wrap;
  };

  const getSubfacet = (fromEl) =>
    fromEl ? fromEl.closest(".laut-subfacet") : document.querySelector(".laut-subfacet");

  const getBaseUrl = () => {
    const subfacet = document.querySelector(".laut-subfacet[data-laut-base-url]");
    return subfacet ? subfacet.getAttribute("data-laut-base-url") : null;
  };

  // Reads active tag handles from URL (/collections/handle/tag1+tag2)
  const getActiveHandlesFromURL = () => {
    const path = window.location.pathname.replace(/\/+$/, "");
    const parts = path.split("/");
    const tagPart = parts.length >= 4 ? parts[3] : "";
    if (!tagPart) return [];
    return tagPart.split("+").filter(Boolean);
  };

  // Current collection handle from URL: /collections/{handle}/...
  const getCollectionHandleFromURL = () => {
    const path = window.location.pathname.replace(/\/+$/, "");
    const parts = path.split("/").filter(Boolean);
    if (parts[0] !== "collections") return "";
    return parts[1] || "";
  };

  // Collection root (no tags): /collections/{handle}
  const getCollectionRootUrl = () => {
    const baseUrl = getBaseUrl();
    if (baseUrl) return baseUrl;

    const handle = getCollectionHandleFromURL();
    if (handle) return `/collections/${handle}`;

    // last-resort fallback
    return "/collections/all-products";
  };

  // Hide subfacet on all-products or when no collection handle exists
  const syncSubfacetVisibility = () => {
    const subfacet = document.querySelector(".laut-subfacet");
    if (!subfacet) return;

    const handle = getCollectionHandleFromURL();
    const shouldHide = !handle || handle === "all-products";

    subfacet.hidden = shouldHide;
  };

  // UPDATED: Show clear button ONLY when at least one tag is active
  const syncClearButton = () => {
    const subfacet = document.querySelector(".laut-subfacet[data-laut-base-url]");
    if (!subfacet) return;

    const clearBtn = subfacet.querySelector("[data-laut-clear]");
    if (!clearBtn) return;

    const activeHandles = getActiveHandlesFromURL();
    clearBtn.hidden = !(activeHandles.length > 0);
  };

  const syncTagUIToURL = () => {
    const subfacet = document.querySelector(".laut-subfacet[data-laut-base-url]");
    if (!subfacet) return;

    const mode = subfacet.getAttribute("data-laut-mode") || "single";
    const activeHandles = getActiveHandlesFromURL();
    const activeSet = new Set(activeHandles);

    subfacet.querySelectorAll(LABEL_SEL).forEach((label) => {
      const handle = label.getAttribute("data-laut-tag-handle");
      const input = label.querySelector(".laut-subfacet__input");

      const isActive =
        mode === "single"
          ? activeHandles.length === 1 && activeHandles[0] === handle
          : activeSet.has(handle);

      if (input) input.checked = !!isActive;
      label.classList.toggle("is-active", !!isActive);
      label.setAttribute("data-laut-tag-active", isActive ? "true" : "false");
    });

    syncClearButton();
  };

  const swapFromHTML = (html) => {
    const frag = parseFragment(html);

    // 1) Swap product grid
    const newGrid = frag.querySelector(GRID_SEL);
    const curGrid = document.querySelector(GRID_SEL);
    if (newGrid && curGrid) curGrid.replaceWith(newGrid);

    // 2) Swap subfacet (so tags regenerate when collection changes)
    const newSubfacet = frag.querySelector(SUBFACET_SEL);
    const curSubfacet = document.querySelector(SUBFACET_SEL);
    if (newSubfacet && curSubfacet) curSubfacet.replaceWith(newSubfacet);

    syncTagUIToURL();
    syncSubfacetVisibility();
    window.dispatchEvent(new Event("resize"));
  };

  const go = async (url, pushState = true) => {
    try {
      setLoading(true);
      const html = await fetchSectionHTML(url);
      swapFromHTML(html);

      if (pushState) {
        window.history.pushState({ url }, "", url);
        syncTagUIToURL();
        syncSubfacetVisibility();
      }

      syncClearButton();
    } catch (e) {
      window.location.href = url;
    } finally {
      setLoading(false);
    }
  };

  const buildSingleUrl = (label) => {
    const baseUrl = getCollectionRootUrl();
    if (!baseUrl) return null;

    const wasActive = label.getAttribute("data-laut-tag-active") === "true";
    if (wasActive) return baseUrl; // toggle reset to collection root

    const handle = label.getAttribute("data-laut-tag-handle");
    return `${baseUrl}/${handle}`;
  };

  const buildMultiUrlFromUI = () => {
    const baseUrl = getCollectionRootUrl();
    if (!baseUrl) return null;

    const checkedHandles = Array.from(document.querySelectorAll(".laut-subfacet__input"))
      .filter((i) => i.checked)
      .map((i) => i.closest(".laut-subfacet__label")?.getAttribute("data-laut-tag-handle"))
      .filter(Boolean);

    if (!checkedHandles.length) return baseUrl;
    return `${baseUrl}/${checkedHandles.join("+")}`;
  };

  const setSingleUIInstant = (label) => {
    const subfacet = getSubfacet(label);
    if (!subfacet) return;

    subfacet.querySelectorAll(".laut-subfacet__input").forEach((i) => (i.checked = false));
    subfacet.querySelectorAll(".laut-subfacet__label").forEach((l) => l.classList.remove("is-active"));

    const wasActive = label.getAttribute("data-laut-tag-active") === "true";
    if (!wasActive) {
      const input = label.querySelector(".laut-subfacet__input");
      if (input) input.checked = true;
      label.classList.add("is-active");
    }
  };

  const setMultiUIInstant = (label) => {
    const input = label.querySelector(".laut-subfacet__input");
    if (!input) return;
    input.checked = !input.checked;
    label.classList.toggle("is-active", input.checked);
  };

  const onActivate = (label) => {
    const subfacet = getSubfacet(label);
    if (!subfacet) return;

    const mode = subfacet.getAttribute("data-laut-mode") || "single";
    let url = null;

    if (mode === "multi") {
      setMultiUIInstant(label);
      url = buildMultiUrlFromUI();
    } else {
      setSingleUIInstant(label);
      url = buildSingleUrl(label);
    }

    if (!url) return;
    go(url, true);
  };

  // Click handler (labels)
  document.addEventListener("click", (e) => {
    const label = e.target.closest(LABEL_SEL);
    if (!label) return;
    e.preventDefault();
    onActivate(label);
  });

  // UPDATED: Click handler (clear button) — clear ONLY tags, keep current collection
  document.addEventListener("click", (e) => {
    const clearBtn = e.target.closest("[data-laut-clear]");
    if (!clearBtn) return;

    e.preventDefault();

    // Instant tag UI reset (ONLY tags)
    const subfacet = document.querySelector(".laut-subfacet[data-laut-base-url]");
    if (subfacet) {
      subfacet.querySelectorAll(".laut-subfacet__input").forEach((i) => (i.checked = false));
      subfacet.querySelectorAll(".laut-subfacet__label").forEach((label) => {
        label.classList.remove("is-active");
        label.setAttribute("data-laut-tag-active", "false");
      });

      const btn = subfacet.querySelector("[data-laut-clear]");
      if (btn) btn.hidden = true;
    }

    // Navigate to collection root (removes /tag1+tag2)
    const rootUrl = getCollectionRootUrl();
    go(rootUrl, true);
  });

  // Keyboard handler (Enter / Space)
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const label = e.target.closest(LABEL_SEL);
    if (!label) return;
    e.preventDefault();
    onActivate(label);
  });

  // Back/forward
  window.addEventListener("popstate", (e) => {
    const url = e.state?.url || window.location.href;
    go(url, false);
  });

  // Initial sync on load
  document.addEventListener("DOMContentLoaded", () => {
    syncTagUIToURL();
    syncSubfacetVisibility();
  });
})();